import { MatchEvent, MatchEventType } from 'shared/types/match-event';
import { RecordingFileRef, VideoMarker } from 'modules/video-markers/domain/interfaces';

/** Насколько начало записи по строке может разойтись с началом файла: доставка, часы, округление. */
export const RECORDING_START_TOLERANCE_MS = 30_000;

const OBS_NAME = /(\d{4})-(\d{2})-(\d{2}) (\d{2})-(\d{2})-(\d{2})/;
const VMIX_NAME = /(\d{1,2}) ([а-яё]+) (\d{4}) - (\d{2})-(\d{2})-(\d{2})/i;
const RU_MONTHS = [
  'января',
  'февраля',
  'марта',
  'апреля',
  'мая',
  'июня',
  'июля',
  'августа',
  'сентября',
  'октября',
  'ноября',
  'декабря',
];

/**
 * Начало записи по имени файла, мс UTC. Время в имени — местное, `utcOffsetMinutes` — пояс машины
 * записи (UTC+7 → `420`).
 *
 * - OBS: `2026-10-08 12-31-13.mp4` — 24-часовой формат, один кандидат.
 * - vMix: `capture - 08 октября 2026 - 02-08-59 .mp4` — часы **12-часовые без AM/PM** (запись в
 *   14:08 названа `02-08-59`), поэтому кандидатов два: `h` и `h + 12`.
 *
 * Имя не узнано — пустой массив.
 */
export function parseRecordingFileStart(fileName: string, utcOffsetMinutes: number): number[] {
  const base = recordingFileName(fileName);
  const toUtc = (year: number, month: number, day: number, hour: number, minute: number, second: number) =>
    Date.UTC(year, month, day, hour, minute, second) - utcOffsetMinutes * 60_000;

  const vmix = VMIX_NAME.exec(base);
  if (vmix) {
    const month = RU_MONTHS.indexOf(vmix[2].toLowerCase());
    if (month === -1) return [];
    const [day, year, hour, minute, second] = [vmix[1], vmix[3], vmix[4], vmix[5], vmix[6]].map(Number);
    const hour12 = hour % 12;
    return [toUtc(year, month, day, hour12, minute, second), toUtc(year, month, day, hour12 + 12, minute, second)];
  }

  const obs = OBS_NAME.exec(base);
  if (obs) {
    const [year, month, day, hour, minute, second] = obs.slice(1).map(Number);
    return [toUtc(year, month - 1, day, hour, minute, second)];
  }

  return [];
}

/**
 * Когда, судя по строке, началась запись: время получения минус позиция записи. `null` — у строки
 * нет позиции записи (программа не писала) или запись стояла на паузе: тогда разность не равна
 * началу файла.
 */
export function recordingStartOfMarker(row: Pick<VideoMarker, 'clientReceivedAt' | 'recordingMs' | 'recordingPaused'>) {
  if (row.recordingMs === null || row.recordingPaused) return null;
  return Date.parse(row.clientReceivedAt) - row.recordingMs;
}

/**
 * Имя файла без папки, для сравнения: путь программы (`C:/Users/…/x.mp4` у OBS, `C:\…\x.mp4` у
 * vMix) и путь, по которому файл открыли при нарезке, совпадают только именем.
 */
export function recordingFileName(filePath: string): string {
  return (filePath.split(/[\\/]/).pop() ?? filePath).toLowerCase();
}

/**
 * Файл, в который писалась строка.
 *
 * - **Строка знает свой файл** (`recordingFile`, миграция `20261009`) — файл с тем же именем, и
 *   только он: время здесь не спасает, а мешает — две записи одного матча на разных машинах
 *   начинаются в пределах допуска. Переименованный файл такие строки поэтому теряет.
 * - **Не знает** (строки до миграции, OBS подключился посреди записи) — по времени: начало записи
 *   по строке ближе всего к началу файла (в пределах `toleranceMs`), и позиция не дальше его
 *   конца. Перезапуск записи начинает `recording_ms` с нуля — такая строка получает новое начало и
 *   уходит в следующий файл.
 *
 * Ни один не подошёл — `null`.
 */
export function matchRecordingFile<T extends RecordingFileRef>(
  row: Pick<VideoMarker, 'clientReceivedAt' | 'recordingMs' | 'recordingPaused' | 'recordingFile'>,
  files: readonly T[],
  toleranceMs = RECORDING_START_TOLERANCE_MS,
): T | null {
  if (row.recordingFile) {
    const name = recordingFileName(row.recordingFile);
    return files.find((file) => recordingFileName(file.path) === name) ?? null;
  }

  const startMs = recordingStartOfMarker(row);
  if (startMs === null) return null;

  let best: T | null = null;
  let bestGap = Infinity;
  for (const file of files) {
    if (file.durationMs !== null && row.recordingMs! > file.durationMs + toleranceMs) continue;
    for (const candidate of file.startCandidatesMs) {
      const gap = Math.abs(candidate - startMs);
      if (gap <= toleranceMs && gap < bestGap) {
        best = file;
        bestGap = gap;
      }
    }
  }

  return best;
}

/** Строки, записанные в этот файл. */
export function markersOfRecording<M extends VideoMarker>(
  markers: readonly M[],
  file: RecordingFileRef,
  toleranceMs = RECORDING_START_TOLERANCE_MS,
): M[] {
  return markers.filter((row) => matchRecordingFile(row, [file], toleranceMs) === file);
}

type RecordedEvent = Pick<MatchEvent, 'id' | 'period' | 'seconds' | 'type'>;

const isGoalType = (type: MatchEventType | null) => type === 'GOAL' || type === 'PASS_AND_GOAL';

/**
 * Гол и гол с ассистом для сопоставления — один тип: бэкенд читает гол со вторым игроком как
 * `PASS_AND_GOAL` (`games/domain/data-mappers/game-details-mapper.ts`), а журнал того же события
 * может нести `GOAL`.
 */
const isSameEventType = (a: MatchEventType | null, b: MatchEventType) => a === b || (isGoalType(a) && isGoalType(b));

/**
 * События журнала, полученные во время записи, — то есть те, у которых среди строк файла есть
 * метка по получению. Журнал матча общий на все записи (прогон IMGM-1484 вёл матч 63759 и в OBS,
 * и в vMix), и без этого фильтра гол из другой записи получил бы позицию по опорам этой и ложный
 * клип.
 *
 * Метка узнаётся по id **или** по типу, тайму и секунде: приложение статиста при повторном
 * открытии матча меняет id старых событий (IMGM-1525), а у событий бэкенда («Моменты») id свои
 * всегда. Узнанному по содержимому событию
 * подставляется id из метки — иначе `resolveEventVideoPosition` её не найдёт. Одна метка
 * достаётся одному событию.
 */
export function selectRecordedEvents<E extends RecordedEvent>(
  events: readonly E[],
  markersOfFile: readonly Pick<VideoMarker, 'kind' | 'eventId' | 'eventType' | 'matchPeriod' | 'matchSecond'>[],
): E[] {
  const receipts = markersOfFile.filter((row) => row.kind === 'event' && row.eventId !== null);
  const taken = new Set<number>();
  const selected: E[] = [];

  const take = (event: E, eventId: number) => {
    taken.add(eventId);
    selected.push(eventId === event.id ? event : { ...event, id: eventId });
  };

  const rest: E[] = [];
  for (const event of events) {
    if (receipts.some((row) => row.eventId === event.id)) take(event, event.id);
    else rest.push(event);
  }

  for (const event of rest) {
    const receipt = receipts.find(
      (row) =>
        !taken.has(row.eventId!) &&
        isSameEventType(row.eventType, event.type) &&
        row.matchPeriod === event.period &&
        row.matchSecond === event.seconds,
    );
    if (receipt) take(event, receipt.eventId!);
  }

  return selected;
}
